import { useApolloClient } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useAnalyticsContext } from "contexts/AnalyticsContext";
import { useAppDispatch } from "hooks/utilsHooks/storeHooks";
import { useCallback } from "react";
import { useIdleTimerContext } from "react-idle-timer";
import { batch } from "react-redux";
import { resetAppState, setAppState } from "slices/appStateSlice";
import { refetchDishTypePrices } from "slices/dishesSlice";
import { finishOrderState } from "slices/transactionSlice";
import { AppState } from "types/mainTypes";

type AwsOrderUpdate = {
	finishOrder: () => void;
};

export const useOrder = (): AwsOrderUpdate => {
	const dispatch = useAppDispatch();
	const idleTimer = useIdleTimerContext();
	const analyticsContext = useAnalyticsContext();
	const apolloClient = useApolloClient();

	const finishOrder = useCallback(() => {
		try {
			// Order completion counts as fresh customer presence: wake the fridge back to ACTIVE and
			// re-arm the inactive-timeout countdown. `activate()` (not `reset()`) restarts the timer AND
			// moves presence to active; the explicit setAppState(ACTIVE) guarantees the lighting converger
			// sees ACTIVE regardless of the idle-timer callback timing. Without this, an order that ends
			// while the customer was tablet-idle (the timeout fired during the order, but an active
			// transaction forbade StandBy) would flip computeDesiredLighting to StandBy the instant the
			// order clears and dim immediately. We want STAND_BY only after a FULL inactive timeout of
			// post-order inactivity.
			idleTimer.activate();
			analyticsContext.reset();
			batch(() => {
				dispatch(finishOrderState());
				dispatch(resetAppState());
				dispatch(setAppState(AppState.ACTIVE));
			});
			dispatch(refetchDishTypePrices(apolloClient));
			return true;
		} catch (error) {
			Sentry.captureException(new Error(`Error while finishing order: ${error}`));
			return false;
		}
	}, [idleTimer, analyticsContext, dispatch, apolloClient]);

	return {
		finishOrder: finishOrder,
	};
};
